import React from 'react';
import NotificationsDropdown from './NotificationsDropdown';
import { calculateAlerts } from './NotificationsDropdown';
import { useFirebaseAuth } from '../auth';
import SiteDetailsModal from './SiteDetailsModal';
import { createLogger } from '../utils/logger';

const logger = createLogger('Header');

export default function Header({ user, onOpenAccountSettings, monitors = [], histories = {}, onNavigate }) {
	const { signOut } = useFirebaseAuth();
	const [notificationsOpen, setNotificationsOpen] = React.useState(false);
	const [showUserMenu, setShowUserMenu] = React.useState(false);
	const [selectedMonitorId, setSelectedMonitorId] = React.useState(null);
	const notificationsRef = React.useRef(null);
	const userMenuRef = React.useRef(null);

	const alerts = React.useMemo(() => {
		return calculateAlerts(monitors, histories);
	}, [monitors, histories]);

	// Get user display name
	const userName = user?.displayName || user?.email?.split('@')[0] || 'User';
	const userFirstName = userName.split(' ')[0];
	const userInitial = userName.charAt(0).toUpperCase();

	// Get time-based greeting
	function getTimeBasedGreeting() {
		const hour = new Date().getHours();
		if (hour >= 5 && hour < 12) {
			return 'Good morning';
		} else if (hour >= 12 && hour < 17) {
			return 'Good afternoon';
		} else if (hour >= 17 && hour < 21) {
			return 'Good evening';
		} else {
			return 'Good evening'; // For late night/early morning, still use evening
		}
	}

	// Close dropdowns when clicking outside
	React.useEffect(() => {
		function handleClickOutside(event) {
			if (notificationsRef.current && !notificationsRef.current.contains(event.target)) {
				setNotificationsOpen(false);
			}
			if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
				setShowUserMenu(false);
			}
		}

		if (notificationsOpen || showUserMenu) {
			document.addEventListener('mousedown', handleClickOutside);
			return () => {
				document.removeEventListener('mousedown', handleClickOutside);
			};
		}
	}, [notificationsOpen, showUserMenu]);

	async function handleSignOut() {
		try {
			await signOut();
			setShowUserMenu(false);
		} catch (error) {
			logger.error('Sign out error:', error);
		}
	}

	return (
		<header className="main-header">
			<div className="header-greeting">{getTimeBasedGreeting()}, {userFirstName}.</div>
			<div className="header-actions">
				<button 
					className="upgrade-btn"
					type="button"
					aria-label="Upgrade your plan"
				>
					<span className="upgrade-icon" aria-hidden="true">★</span>
					Upgrade your plan
				</button>
				<div className="notifications-wrapper" ref={notificationsRef}>
					<button
						className={`icon-btn ${notificationsOpen ? 'active' : ''}`}
						type="button"
						onClick={() => setNotificationsOpen(!notificationsOpen)}
						aria-label={`Notifications${alerts.length > 0 ? ` (${alerts.length} unread)` : ''}`}
						aria-expanded={notificationsOpen}
						aria-haspopup="true"
					>
						<span className="material-icons" aria-hidden="true">notifications</span>
						{alerts.length > 0 && (
							<span className="notification-badge" aria-label={`${alerts.length} unread notifications`}>
								{alerts.length}
							</span>
						)}
					</button>
					{notificationsOpen && (
						<NotificationsDropdown
							alerts={alerts}
							onClose={() => setNotificationsOpen(false)}
							onNavigate={onNavigate}
							onOpenSiteDetails={(monitorId) => {
								setSelectedMonitorId(monitorId);
								setNotificationsOpen(false);
							}}
						/>
					)}
				</div>
				<div className="user-menu-wrapper" ref={userMenuRef} style={{ position: 'relative' }}>
					<button
						className="profile-avatar"
						type="button"
						onClick={() => setShowUserMenu(!showUserMenu)}
						aria-label={`User menu for ${userName}`}
						aria-expanded={showUserMenu}
						aria-haspopup="true"
					>
						<span className="avatar-text" aria-hidden="true">{userInitial}</span>
					</button>
					{showUserMenu && (
						<div
							role="menu"
							aria-label="User account menu"
							style={{
								position: 'absolute',
								right: 0,
								top: 'calc(100% + 8px)',
								background: 'var(--panel)',
								border: '1px solid var(--border)',
								borderRadius: '8px',
								padding: '8px',
								minWidth: '160px',
								boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
								zIndex: 1000,
							}}
						>
							<div
								style={{
									padding: '8px 12px',
									fontSize: '14px',
									borderBottom: '1px solid var(--border)',
									marginBottom: '4px',
								}}
							>
								<div style={{ fontWeight: 500 }}>{user?.displayName || 'User'}</div>
								{user?.email && (
									<div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '2px' }}>
										{user.email}
									</div>
								)}
							</div>
							<button
								className="btn"
								role="menuitem"
								type="button"
								style={{ width: '100%', justifyContent: 'flex-start' }}
								onClick={() => {
									setShowUserMenu(false);
									onOpenAccountSettings && onOpenAccountSettings();
								}}
							>
								Settings
							</button>
							<button
								className="btn"
								role="menuitem"
								type="button"
								style={{ width: '100%', justifyContent: 'flex-start', marginTop: '4px' }}
								onClick={handleSignOut}
							>
								Log out
							</button>
						</div>
					)}
				</div>
			</div>
			{selectedMonitorId && (
				<SiteDetailsModal 
					monitorId={selectedMonitorId} 
					onClose={() => setSelectedMonitorId(null)} 
				/>
			)}
		</header>
	);
}

